import { getRangePickerValue } from '@intelligence/v3/filterControls/rangePicker/utils';
import { getStringArrayValue } from '@intelligence/v3/filterControls/shared/valueUtils';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import { buildRobloxGenreFilterParam } from '@intelligence/v3/utils/buildRobloxGenreFilter';
import {
  getDefaultFilterValues,
  ROBLOX_GAME_RANK_DOWNLOAD_GROUP_BY,
  ROBLOX_GAME_RANK_DOWNLOAD_PAGE_SIZE,
  ROBLOX_GAME_RANK_DOWNLOAD_USE_SSE_PAGE,
  ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS,
} from '../constants';

import type { IRobloxGameRankDownloadFormValues } from '../components/RobloxGameRankDownload/types';
import type { IRobloxGameRankFilterValues } from '../types';
import type { TRobloxDownloadType } from '../components/RobloxGameRankDownload/types';
import type { IMainGenre } from '@intelligence/types/common';

interface IBuildRobloxGameRankDownloadParamsOptions {
  filterValues: IRobloxGameRankFilterValues;
  formValues: IRobloxGameRankDownloadFormValues;
  regionOptions?: IRecord[];
  robloxGenreOptions?: IMainGenre[];
}

const mapDownloadTopType = (
  selectTopType: TRobloxDownloadType,
): 'game' | 'date' => {
  return selectTopType === 'game' ? 'game' : 'date';
};

const buildDownloadTopParam = (
  dataMethod: IRobloxGameRankDownloadFormValues['data_method'],
  selectTop: number,
  selectTopType: TRobloxDownloadType,
): IRecord | undefined => {
  if (dataMethod !== 'selectTop' || selectTop <= 0) {
    return undefined;
  }

  return {
    top_type: mapDownloadTopType(selectTopType),
    size: selectTop,
  };
};

export const buildRobloxGameRankDownloadParams = (
  options: IBuildRobloxGameRankDownloadParamsOptions,
) => {
  const { filterValues, formValues, regionOptions = [], robloxGenreOptions } = options;
  const defaults = getDefaultFilterValues();
  const [startDate, endDate] = formValues.dateRange;
  const selectedSources = formValues.sources.filter(Boolean);
  const genreFilter = buildRobloxGenreFilterParam(
    filterValues.genre ?? defaults.genre,
    robloxGenreOptions,
  );
  const topParam = buildDownloadTopParam(
    formValues.data_method,
    formValues.data_amount,
    formValues.select_top_type,
  );

  const params: IRecord = {
    group_by: [...ROBLOX_GAME_RANK_DOWNLOAD_GROUP_BY],
    use_entity_type: ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS.use_entity_type,
    use_custom_fields: [...ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS.use_custom_fields],
    order_by: ['date asc', 'rank asc'],
    sources: selectedSources,
    start_date: startDate,
    end_date: endDate,
    markets: getStringArrayValue(filterValues.region ?? defaults.region),
    devices: getStringArrayValue(filterValues.device ?? defaults.device),
    page: 1,
    page_size: ROBLOX_GAME_RANK_DOWNLOAD_PAGE_SIZE,
    filter: genreFilter,
    dateRange: formValues.dateRange,
    data_method: formValues.data_method,
    data_amount: formValues.data_amount,
    [DOWNLOAD_CFG_KEY.DATA_METHOD]: formValues.data_method,
    [DOWNLOAD_CFG_KEY.SELECT_TOP]: formValues.data_amount,
    select_top_type: formValues.select_top_type,
    api_top_applied: Boolean(topParam),
    file_format: formValues.file_format === 'xlsx' ? 0 : 1,
    device_show: formValues.gameInfo.device_show ? 1 : 0,
    genre_show: formValues.gameInfo.genre_show ? 1 : 0,
    sources_order: selectedSources,
    regionOptions,
    filterValues,
    new_style_donwload_file_name: 'Store Ranks_Roblox_Detail',
  };

  if (formValues.data_method === 'checkedLines' && formValues.game_ids.length) {
    params.game_ids = formValues.game_ids;
  }

  return params;
};

export const buildRobloxGameRankDownloadApiParams = (
  params: IRecord = {},
): IRecord => {
  const dataMethod =
    params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] ?? params?.data_method;
  const selectTop =
    Number(params?.[DOWNLOAD_CFG_KEY.SELECT_TOP] ?? params?.data_amount) || 0;
  const selectTopType = params?.select_top_type || 'time';

  const apiParams: IRecord = {
    start_date: params.start_date,
    end_date: params.end_date,
    group_by: params.group_by || [...ROBLOX_GAME_RANK_DOWNLOAD_GROUP_BY],
    use_entity_type: params.use_entity_type,
    use_custom_fields:
      params.use_custom_fields ||
      [...ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS.use_custom_fields],
    markets: params.markets,
    devices: params.devices,
    page: params.page || 1,
    page_size: ROBLOX_GAME_RANK_DOWNLOAD_PAGE_SIZE,
    order_by: params.order_by || ['date asc', 'rank asc'],
    filter: params.filter || {},
    use_sse_page: { ...ROBLOX_GAME_RANK_DOWNLOAD_USE_SSE_PAGE },
  };

  if (Array.isArray(params.sources) && params.sources.length) {
    apiParams.sources = params.sources;
  }

  if (Array.isArray(params.game_ids) && params.game_ids.length) {
    apiParams.game_ids = params.game_ids;
  }

  const topParam = buildDownloadTopParam(dataMethod, selectTop, selectTopType);
  if (topParam) {
    apiParams.top = topParam;
  }

  return apiParams;
};

export const getRobloxDownloadDefaultDateRange = (
  filterValues: IRobloxGameRankFilterValues,
): [string, string] => {
  const { startDate, endDate } = getRangePickerValue(filterValues.date);
  return [startDate || endDate, endDate || startDate];
};
